import { getStrbufSummary } from '../str-buf/getStrbufSummary';
import { guessEncoding } from '../str-buf/guessEncoding';
import { StringDecoder } from '../types';
import { normalizeCharset2 } from './charsets';
import { qpDecode } from './qp-decode';

/**
 * Decode a complete mime word encoded string
 *
 * @param {String} str Mime word encoded string
 * @return {String} Decoded unicode string
 */
function _decodeWord(str: string, decode: StringDecoder): string {
	str = str.trim();

	const match = str.match(/^=\?([\w_\-*]+)\?([QqBb])\?([^]*?)\?=$/i);
	if (!match) {
		return str;
	}

	// RFC2231 added language tag to the encoding
	// see: https://tools.ietf.org/html/rfc2231#section-5
	// this implementation silently ignores this tag
	const fromCharset = match[1].split('*').shift();

	const encoding = (match[2] || 'Q').toString().toUpperCase();
	str = match[3] || '';

	if (encoding === 'Q') {
		// remove spaces between = and hex char, this might indicate invalidly applied line splitting
		str = str.replace(/=\s+([0-9a-fA-F])/, '=$1');
	}

	// convert all underscores to spaces
	str = str.replace(/_/g, ' ').replace(/ $/, '=20');

	if (encoding === 'B') {
		// The word may contain multiple Base64 strings that are concatenated together with = padding
		// We can't just remove this padding - each part has to be be Base64-decoded separately
		const bufs: Buffer[] = [];

		const split = str.split('=');
		for (const part of split) {
			if (part) {
				bufs.push(Buffer.from(part, 'base64'));
			}
		}

		return decode(bufs.length === 1 ? bufs[0] : Buffer.concat(bufs), fromCharset);
	} else if (encoding === 'Q') {
		return decode(qpDecode(str), fromCharset);
	} else {
		return str;
	}
}

/**
 * Decode a string that might include one or several mime words
 *
 * @param {String} str String including some mime words that will be encoded
 * @return {String} Decoded unicode string
 */
function _decodeWords(str: string, decode: StringDecoder): string {
	// find base64 words that can be joined
	const s2 = str.replace(
		/(=\?([^?]+)\?[Bb]\?[^]*?\?=)\s*(?==\?([^?]+)\?[Bb]\?[^]*?\?=)/g,
		(match, left, chLeft, chRight) => {
			// only mark b64 chunks to be joined if charsets match
			if (normalizeCharset2(chLeft || '') === normalizeCharset2(chRight || '')) {
				// set a joiner marker
				return left + '__\x00JOIN\x00__';
			}
			return match;
		}
	);

	// find QP words that can be joined
	const s3 = s2.replace(
		/(=\?([^?]+)\?[Qq]\?[^]*?\?=)\s*(?==\?([^?]+)\?[Qq]\?[^]*?\?=)/g,
		(match, left, chLeft, chRight) => {
			// only mark QP chunks to be joined if charsets match
			if (normalizeCharset2(chLeft || '') === normalizeCharset2(chRight || '')) {
				// set a joiner marker
				return left + '__\x00JOIN\x00__';
			}
			return match;
		}
	);

	// join base64 encoded words
	const s4 = s3.replace(/(\?=)?__\x00JOIN\x00__(=\?([^?]+)\?[QqBb]\?)?/g, '');

	// remove spaces between mime encoded words
	const s5 = s4.replace(/(=\?[^?]+\?[QqBb]\?[^?]*\?=)\s+(?==\?[^?]+\?[QqBb]\?[^?]*\?=)/g, '$1');

	// decode words
	const s6 = s5.replace(/=\?([\w_\-*]+)\?([QqBb])\?[^]*?\?=/g, mimeWord =>
		_decodeWord(mimeWord, decode)
	);

	return s6;
}

/**
 * Handles words with "UNKNOWN" encoding
 * @param str
 * @param decode
 */
export function decodeWords(str: string, decode: StringDecoder): string {
	return _decodeWords(str, (buf, enc) => {
		if (!buf.length) {
			return '';
		}

		const enc2 = normalizeCharset2(enc ?? '');
		if (enc2 === 'unknown' || enc2 === 'undefined') {
			const [enc3, hasBom] = guessEncoding(buf);

			if (enc3 === 'ascii' || enc3 === 'utf8') {
				let str2 = decode(buf, enc3);

				// Strip BOM? Has not explicitly been seen / detected but is imaginable...?
				if (hasBom && str2.startsWith('\uFEFF')) {
					str2 = str2.slice(1);
				}

				return str2;
			} else if (enc3 === '8bit') {
				// Get histo
				const aggs = getStrbufSummary(buf);
				if (
					!aggs.hasNull &&
					!aggs.hasControl &&
					aggs.hasHigh &&
					// Check for extra cp1252 controls
					!aggs.histo[0x81] &&
					!aggs.histo[0x8d] &&
					!aggs.histo[0x8f] &&
					!aggs.histo[0x90] &&
					!aggs.histo[0x9d]
				) {
					// Just use cp1252?
					return decode(buf, 'cp1252');
				}
			}
		}

		return decode(buf, enc2);
	});
}
